import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Save, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Alert, Button, Card, DataField, FormField, Input, PageContainer, PageHeader, Select } from "@/components/ui";

// ============================================================================
// Tipos
// ============================================================================

interface SolicitudDetalle {
  id: string;
  folio: string;
  servicioCodigo: string | null;
  servicioNombre: string | null;
  esquemaCodigo: string | null;
  tipoProductoCodigo: string | null;
  titularRazonSocial: string;
  titularRfc: string;
  tramitadorRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
  productoModelos: string | null;
  productoFabricante: string | null;
  productoFraccionArancelaria: string | null;
  productoCantidadLote: number | null;
  productoNumImeis: number | null;
  paisesOrigen: { codigo: string; nombre: string }[];
  paisesProcedencia: { codigo: string; nombre: string }[];
  numFamiliaGrupos: string | null;
  /** Número de Referencia (UPT{AA}-{NNNN}) capturado por Finanzas en PAGO_VALIDADO. */
  numReferencia: string | null;
  normas: {
    id: string;
    normaId: string;
    normaCodigo: string;
    normaTitulo: string;
    laboratorioId: string | null;
    laboratorioRazonSocial: string | null;
    /** Datos del reporte que ya fueron capturados al crear la solicitud Telecom. */
    numReporte: string | null;
    modelosEvaluados: string | null;
    fechaEmision: string | null;
    diasVigencia: number | null;
    numImeis: number | null;
  }[];
}

interface EspTecCatalogo {
  id: string;
  codigo: string;
  nombre: string;
  consec: number;
  tipoUi: "TEXTAREA" | "TEXTAREA_3COLS" | "SELECT_BANDA_28" | "SELECT_VOLTE" | "BOOLEANO_FM" | "DINAMICA_BANDA_SAR" | "ET_VALOR";
  unidades: string[];
  valorDefault: string | null;
  dinamica: boolean;
  /**
   * Defaults por columna de las tablas de tres columnas. El formato oficial
   * fija, renglón por renglón, en cuál va "N/A": no es lo mismo para todas.
   * Son valores precargados y editables, no celdas bloqueadas.
   */
  defaultSaltoFrec: string | null;
  defaultModDigital: string | null;
  defaultHibrido: string | null;
}

interface Acreditacion {
  id: string;
  tipo: "EMA" | "DGN" | "CONUEE" | "IFT";
  identificador: string;
  fechaEmision: string | null;
}

interface EspecForm {
  /** id temporal de UI */
  uiId: string;
  normaEspTecId: string | null;
  codigo: string | null;
  nombre: string | null;
  tipoUi: EspTecCatalogo["tipoUi"];
  consec: number;
  unidades: string[];
  valor: string;
  saltoFrec: string;
  modulacionDigital: string;
  hibrido: string;
  bandaFrecuencia: string;
  sar: string;
}

interface NormaForm {
  normaId: string;
  normaCodigo: string;
  laboratorioId: string | null;
  laboratorioRazonSocial: string | null;
  numReporte: string;
  fechaReporte: string;
  modelosEvaluados: string;
  receptorFm: boolean | null;
  acreditacionesIds: string[];
  especificaciones: EspecForm[];
}

interface FraccionForm {
  uiId: string;
  certificadoText: string;
  fraccionArancelaria: string;
  nico: string;
  consec: number;
  /** Último valor auto-sincronizado con numCertificado. Permite detectar si el usuario lo editó manualmente. */
  __autoCert?: string;
}

const NOM_221_P1 = "NOM-221-SCFI-2017-P1";

// ============================================================================
// Página
// ============================================================================

export function CertificadoTelecomAltaPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const solicitudId = params.get("solicitudId");

  const qSolicitud = useQuery<SolicitudDetalle>({
    queryKey: ["solicitud-detalle-completo", solicitudId],
    queryFn: async () => (await api.get<SolicitudDetalle>(`/api/sector/telecom/solicitudes/${solicitudId}/detalle-completo`)).data,
    enabled: !!solicitudId,
  });

  // num_certificado propuesto por el backend (consecutivo anual del OC).
  // Idempotente — si ya hay certificado, devuelve el num existente.
  const qNumeracion = useQuery<{
    numCertificadoSugerido: string;
    esAmpliacion: boolean;
    sufijoAmpliacion: string;
    consecutivo: number;
    anio: number;
  }>({
    queryKey: ["proxima-numeracion-certificado", solicitudId],
    queryFn: async () => (await api.get(`/api/sector/telecom/certificados/proxima-numeracion`, { params: { solicitudId } })).data,
    enabled: !!solicitudId,
  });

  const [numCertificado, setNumCertificado] = useState("");
  const [fechaEmision, setFechaEmision] = useState<string>(new Date().toISOString().slice(0, 10));
  const [modalidad, setModalidad] = useState<"" | "CON_RELACION" | "SIN_RELACION">("");
  const [normasForm, setNormasForm] = useState<NormaForm[]>([]);
  const [fracciones, setFracciones] = useState<FraccionForm[]>([]);
  const [error, setError] = useState<string | null>(null);

  const tieneNom221P1 = useMemo(
    () => qSolicitud.data?.normas.some((n) => n.normaCodigo === NOM_221_P1) ?? false,
    [qSolicitud.data],
  );

  // Inicializa el form al cargar la solicitud, heredando los datos del
  // reporte de laboratorio capturados al crear la solicitud Telecom.
  useEffect(() => {
    if (!qSolicitud.data || normasForm.length > 0) return;
    setNormasForm(
      qSolicitud.data.normas.map((n) => ({
        normaId: n.normaId,
        normaCodigo: n.normaCodigo,
        laboratorioId: n.laboratorioId,
        laboratorioRazonSocial: n.laboratorioRazonSocial,
        numReporte: n.numReporte ?? "",
        fechaReporte: n.fechaEmision ?? new Date().toISOString().slice(0, 10),
        modelosEvaluados: n.modelosEvaluados ?? qSolicitud.data!.productoModelo,
        receptorFm: n.normaCodigo === NOM_221_P1 ? false : null,
        acreditacionesIds: [],
        especificaciones: [],
      })),
    );

    // Pre-poblar fracciones arancelarias con los valores capturados en la
    // solicitud, separados por coma. Cada una en su propia fila editable.
    // Excepción (incidencia 0049): los trámites que NO modifican fracciones
    // (reexpedición, ampliación de modelos/países/filiales) las heredan del
    // certificado origen con su NICO en el backend — aquí no se capturan.
    const servicioPre = qSolicitud.data.servicioCodigo;
    const heredaFraccionesPre =
      servicioPre === "REEXPEDICION_PC" ||
      (!!servicioPre && servicioPre.startsWith("AMPLIACION") && servicioPre !== "AMPLIACION_FRACCION");
    const fraccionesRaw = qSolicitud.data.productoFraccionArancelaria;
    if (!heredaFraccionesPre && fraccionesRaw && fraccionesRaw.trim().length > 0) {
      const partes = fraccionesRaw.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
      setFracciones(partes.map((p, i) => ({
        uiId: Math.random().toString(36).slice(2),
        certificadoText: "",
        fraccionArancelaria: p,
        nico: "",
        consec: i + 1,
      })));
    }
  }, [qSolicitud.data, normasForm.length]);

  // Prefill del num_certificado cuando llega la respuesta del backend. Solo
  // sobreescribimos si el usuario no editó manualmente (campo aún vacío).
  useEffect(() => {
    if (!qNumeracion.data) return;
    if (numCertificado === "") setNumCertificado(qNumeracion.data.numCertificadoSugerido);
  }, [qNumeracion.data]);

  const servicioActual = qSolicitud.data?.servicioCodigo;
  const heredaFracciones =
    servicioActual === "REEXPEDICION_PC" ||
    (!!servicioActual && servicioActual.startsWith("AMPLIACION") && servicioActual !== "AMPLIACION_FRACCION");
  const fraccionesHeredadas = (qSolicitud.data?.productoFraccionArancelaria ?? "")
    .split(/[,;]/).map((x) => x.trim()).filter(Boolean).length;

  // Cuando cambia el número del certificado arriba, sincroniza al
  // certificadoText de las filas que aún no lo han editado manualmente.
  useEffect(() => {
    setFracciones((arr) => arr.map((f) =>
      f.certificadoText === "" || f.certificadoText === f.__autoCert
        ? { ...f, certificadoText: numCertificado, __autoCert: numCertificado }
        : f,
    ));
  }, [numCertificado]);

  const crear = useMutation({
    mutationFn: async () => {
      const body = {
        solicitudId,
        numCertificado: numCertificado.trim().toUpperCase(),
        fechaEmision: fechaEmision || null,
        modalidad: tieneNom221P1 ? modalidad : null,
        normas: normasForm.map((n) => ({
          normaId: n.normaId,
          laboratorioId: n.normaCodigo === NOM_221_P1 ? null : n.laboratorioId,
          numReporte: n.numReporte.trim() || null,
          fechaReporte: n.fechaReporte || null,
          modelosEvaluados: n.modelosEvaluados.trim() || null,
          receptorFm: n.normaCodigo === NOM_221_P1 ? n.receptorFm : null,
          acreditacionesIds: n.acreditacionesIds,
          especificaciones: n.especificaciones.map((e) => ({
            normaEspTecId: e.normaEspTecId,
            consec: e.consec,
            valor: e.valor.trim() || null,
            saltoFrec: e.saltoFrec.trim() || null,
            modulacionDigital: e.modulacionDigital.trim() || null,
            hibrido: e.hibrido.trim() || null,
            bandaFrecuencia: e.bandaFrecuencia.trim() || null,
            sar: e.sar.trim() || null,
          })),
        })),
        fracciones: heredaFracciones ? [] : fracciones.map((f) => ({
          certificadoText: f.certificadoText.trim() || null,
          fraccionArancelaria: f.fraccionArancelaria.trim(),
          nico: f.nico.trim() || null,
          consec: f.consec,
        })),
      };
      const { data } = await api.post<{ id: string }>("/api/sector/telecom/certificados", body);
      return data.id;
    },
    onSuccess: (id) => navigate(`/telecom/certificados/${id}/success`),
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string; message?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.message || r.response?.data?.title || (e as Error).message);
    },
  });

  const submit = () => {
    setError(null);
    if (!numCertificado.trim()) return setError("Captura el número de certificado.");
    if (!fechaEmision) return setError("Captura la fecha de emisión.");
    if (tieneNom221P1 && !modalidad) return setError("Selecciona la modalidad (NOM-221 P1).");
    for (const n of normasForm) {
      if (n.normaCodigo === NOM_221_P1) continue;
      if (!n.numReporte.trim()) return setError(`Captura el número de reporte para ${n.normaCodigo}.`);
      if (n.acreditacionesIds.length === 0) return setError(`Selecciona al menos una acreditación para ${n.normaCodigo}.`);
    }
    crear.mutate();
  };

  if (!solicitudId) {
    return (
      <PageContainer width="form">
        <Alert variant="warning">
          Falta el parámetro <code>solicitudId</code> en la URL.
          {" "}<Link to="/telecom/certificados/por-certificar" className="font-medium underline">Volver a "Por certificar"</Link>.
        </Alert>
      </PageContainer>
    );
  }
  if (qSolicitud.isLoading) return <div className="py-20 text-center text-sm text-fg-subtle">Cargando solicitud…</div>;
  if (qSolicitud.error || !qSolicitud.data) {
    return <Alert variant="warning">No se pudo cargar la solicitud.</Alert>;
  }
  const s = qSolicitud.data;

  return (
    <PageContainer width="wide">
      <PageHeader
        backTo="/telecom/certificados/por-certificar"
        backLabel='Volver a "Por certificar"'
        title={
          s.servicioCodigo === "REEXPEDICION_PC"
            ? "Elaborar Certificado Reexpedido"
            : s.servicioCodigo === "AMPLIACION_PAISES"
              ? "Elaborar Certificado (Ampliación / Reducción de Países)"
              : s.servicioCodigo?.startsWith("AMPLIACION")
                ? "Elaborar Certificado (Ampliación / Reducción)"
                : "Elaborar Certificado de Conformidad Único"
        }
        subtitle={<>Solicitud <span className="font-mono">{s.folio}</span> · {s.titularRazonSocial}</>}
      />

      {/* Datos del certificado */}
      <Section title="Información del certificado">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="No. de certificado *">
            <Input value={numCertificado} onChange={(e) => setNumCertificado(e.target.value.toUpperCase())}
                   maxLength={40} placeholder="NOR26130TU0000" className="font-mono uppercase" />
            {qNumeracion.data?.esAmpliacion && (
              <div className="mt-0.5 text-[11px] text-estado-warn">
                {s.servicioCodigo === "REEXPEDICION_PC" ? "Reexpedición" : "Ampliación detectada"} — sufijo {qNumeracion.data.sufijoAmpliacion}
              </div>
            )}
          </Field>
          <Field label="Fecha de emisión *">
            <Input type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} />
          </Field>
          <Field label="Núm. de referencia (Finanzas)">
            <Input value={s.numReferencia ?? "—"} readOnly disabled className="font-mono" />
          </Field>
        </div>
        {tieneNom221P1 && (
          <Field label="Modalidad * (NOM-221 P1)">
            <Select value={modalidad} onChange={(e) => setModalidad(e.target.value as "" | "CON_RELACION" | "SIN_RELACION")}>
              <option value="">— Selecciona —</option>
              <option value="CON_RELACION">Con relación</option>
              <option value="SIN_RELACION">Sin relación</option>
            </Select>
          </Field>
        )}
      </Section>

      {/* Datos heredados de la solicitud (read-only) */}
      <Section title="Datos del solicitante (heredados de la solicitud)">
        <div className="grid gap-3 sm:grid-cols-2">
          <ReadField label="Razón social" value={s.titularRazonSocial} />
          <ReadField label="RFC" value={s.titularRfc} mono />
          {s.tramitadorRazonSocial && <ReadField label="Tramitador" value={s.tramitadorRazonSocial} />}
          <ReadField label="Esquema" value={s.esquemaCodigo ?? "—"} />
          <ReadField label="Tipo de producto" value={s.tipoProductoCodigo ?? "—"} />
          <ReadField label="Producto" value={`${s.productoNombre} ${s.productoModelo}`} />
          <ReadField label="Marca" value={s.marca} />
          {s.productoFabricante && <ReadField label="Fabricante" value={s.productoFabricante} />}
          {s.productoNumImeis != null && <ReadField label="Núm. de IMEIs" value={s.productoNumImeis.toLocaleString("es-MX")} />}
        </div>
      </Section>

      {/* Aviso de alcance acumulado para reexpedición */}
      {s.servicioCodigo === "REEXPEDICION_PC" && (
        <Alert variant="info">
          Reexpedición por pruebas complementarias. Aquí capturas la información técnica de las
          <strong> NOM/DT adicionales</strong>; las normas del certificado original se incorporan
          automáticamente al emitir, conformando el alcance acumulado. El número se asignará como
          <span className="font-mono"> {qNumeracion.data?.numCertificadoSugerido ?? "…-A{n}"}</span>.
        </Alert>
      )}

      {/* Aviso de alcance heredado para ampliación de países */}
      {s.servicioCodigo === "AMPLIACION_PAISES" && (
        <Alert variant="info">
          Ampliación / Reducción de Países. El alcance técnico del certificado original (normas y
          dictámenes) se incorpora automáticamente al emitir; el cambio de países ya está aplicado
          sobre los datos heredados. El número se asignará como
          <span className="font-mono"> {qNumeracion.data?.numCertificadoSugerido ?? "…-A{n}"}</span>
          {" "}y el certificado original quedará SUSTITUIDO.
        </Alert>
      )}

      {/* Normas */}
      {normasForm.map((n, idx) => (
        <NormaSection
          key={n.normaId}
          norma={n}
          onChange={(next) => setNormasForm((arr) => arr.map((x, i) => (i === idx ? next : x)))}
        />
      ))}

      {/* Fracciones arancelarias — heredadas para trámites que no las modifican */}
      {heredaFracciones ? (
        <Section title="Fracciones arancelarias">
          <div className="rounded-lg border border-edge bg-surface-2/60 p-3 text-sm text-fg-muted">
            Este trámite no modifica las fracciones arancelarias: se heredarán del
            certificado origen con su NICO
            {fraccionesHeredadas > 0 ? ` (${fraccionesHeredadas} fracción${fraccionesHeredadas === 1 ? "" : "es"})` : ""}.
          </div>
        </Section>
      ) : (
      <Section title={`Fracciones arancelarias${fracciones.length > 0 ? ` (${fracciones.length})` : ""}`}>
        <Button
          size="sm"
          variant="secondary"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setFracciones((arr) => [...arr, {
            uiId: Math.random().toString(36).slice(2),
            certificadoText: numCertificado,
            __autoCert: numCertificado,
            fraccionArancelaria: "",
            nico: "",
            consec: arr.length + 1,
          }])}
        >
          Agregar fracción
        </Button>
        {fracciones.length === 0 ? (
          <div className="rounded-lg border border-dashed border-edge-strong p-3 text-sm text-fg-subtle">
            Aún no agregas fracciones arancelarias.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-left">Certificado</th>
                <th className="px-3 py-2 text-left">Fracción arancelaria *</th>
                <th className="px-3 py-2 text-left">NICO</th>
                <th className="w-12 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-edge">
              {fracciones.map((f, i) => (
                <tr key={f.uiId}>
                  <td className="px-3 py-2">
                    <input value={f.certificadoText} maxLength={20}
                           onChange={(e) => setFracciones((arr) => arr.map((x, idx2) => idx2 === i ? { ...x, certificadoText: e.target.value } : x))}
                           className="w-full rounded-md border border-edge-strong px-2 py-1.5 text-xs" />
                  </td>
                  <td className="px-3 py-2">
                    <input value={f.fraccionArancelaria} maxLength={8}
                           onChange={(e) => setFracciones((arr) => arr.map((x, idx2) => idx2 === i ? { ...x, fraccionArancelaria: e.target.value.replace(/\D/g, "") } : x))}
                           className="w-full rounded-md border border-edge-strong px-2 py-1.5 text-xs font-mono" />
                  </td>
                  <td className="px-3 py-2">
                    <input value={f.nico} maxLength={3}
                           onChange={(e) => setFracciones((arr) => arr.map((x, idx2) => idx2 === i ? { ...x, nico: e.target.value.replace(/\D/g, "") } : x))}
                           className="w-full rounded-md border border-edge-strong px-2 py-1.5 text-xs font-mono" />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => setFracciones((arr) => arr.filter((_, idx2) => idx2 !== i))}
                            className="cursor-pointer rounded p-1 text-estado-err transition hover:bg-estado-err-bg"><Trash2 className="h-3 w-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Section>
      )}

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" disabled={crear.isPending}
                onClick={() => navigate("/telecom/certificados/por-certificar")}>
          Cancelar
        </Button>
        <Button loading={crear.isPending} icon={<Save className="h-4 w-4" />} onClick={submit}>
          {crear.isPending ? "Creando…" : "Crear certificado"}
        </Button>
      </div>
    </PageContainer>
  );
}

// ============================================================================
// Subcomponentes
// ============================================================================

function NormaSection({ norma, onChange }: { norma: NormaForm; onChange: (n: NormaForm) => void }) {
  const qEspTec = useQuery<EspTecCatalogo[]>({
    queryKey: ["norma-esp-tec", norma.normaId],
    queryFn: async () =>
      (await api.get<EspTecCatalogo[]>(`/api/sector/telecom/normas/${norma.normaId}/especificaciones-tecnicas`)).data,
  });
  const qAccs = useQuery<Acreditacion[]>({
    queryKey: ["lab-accs", norma.laboratorioId],
    queryFn: async () =>
      (await api.get<Acreditacion[]>(`/api/sector/telecom/laboratorios/${norma.laboratorioId}/acreditaciones`)).data,
    enabled: !!norma.laboratorioId,
  });

  // Inicializar especificaciones desde el catálogo (filas no dinámicas).
  useEffect(() => {
    if (!qEspTec.data || norma.especificaciones.length > 0) return;
    const inicial: EspecForm[] = qEspTec.data
      .filter((e) => !e.dinamica)
      .map((e) => ({
        uiId: Math.random().toString(36).slice(2),
        normaEspTecId: e.id,
        codigo: e.codigo,
        nombre: e.nombre,
        tipoUi: e.tipoUi,
        consec: e.consec,
        unidades: e.unidades ?? [],
        valor: e.valorDefault ?? "",
        // Cada columna toma su propio default del catálogo. Antes se volcaba
        // el único valorDefault siempre en Salto de frecuencia, que es justo
        // donde va el valor medido.
        saltoFrec: e.defaultSaltoFrec ?? "",
        modulacionDigital: e.defaultModDigital ?? "",
        hibrido: e.defaultHibrido ?? "",
        bandaFrecuencia: "",
        sar: "",
      }));
    onChange({ ...norma, especificaciones: inicial });
  }, [qEspTec.data, norma, onChange]);

  const esNom221P1 = norma.normaCodigo === NOM_221_P1;
  const accsEMA = qAccs.data?.filter((a) => a.tipo === "EMA") ?? [];
  const accsIFT = qAccs.data?.filter((a) => a.tipo === "IFT") ?? [];
  const accsDgnConuee = qAccs.data?.filter((a) => a.tipo === "DGN" || a.tipo === "CONUEE") ?? [];

  const toggleAcc = (id: string) => {
    const cur = norma.acreditacionesIds;
    onChange({
      ...norma,
      acreditacionesIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    });
  };

  return (
    <Section title={`${norma.normaCodigo}`}>
      {/* Cabecera laboratorio (oculta para NOM-221-P1) */}
      {!esNom221P1 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <ReadField label="Laboratorio" value={norma.laboratorioRazonSocial ?? "Sin asignar"} />
            <Field label="No. de reporte *">
              <Input value={norma.numReporte} onChange={(e) => onChange({ ...norma, numReporte: e.target.value.toUpperCase() })}
                     className="uppercase" />
            </Field>
            <Field label="Fecha de reporte *">
              <Input type="date" max={new Date().toISOString().slice(0, 10)} value={norma.fechaReporte}
                     onChange={(e) => onChange({ ...norma, fechaReporte: e.target.value })} />
            </Field>
            <Field label="Modelos evaluados">
              <Input value={norma.modelosEvaluados} onChange={(e) => onChange({ ...norma, modelosEvaluados: e.target.value })} />
            </Field>
          </div>

          <div className="mt-3 space-y-2">
            <AcreditacionGroup label="Acreditación (EMA)" items={accsEMA} selected={norma.acreditacionesIds} onToggle={toggleAcc} />
            <AcreditacionGroup label="Autorización (IFT)" items={accsIFT} selected={norma.acreditacionesIds} onToggle={toggleAcc} />
            <AcreditacionGroup label="Aprobación (DGN / CONUEE)" items={accsDgnConuee} selected={norma.acreditacionesIds} onToggle={toggleAcc} />
          </div>
        </>
      )}

      {/* Especificaciones técnicas según tipo_ui */}
      <div className="mt-4">
        {qEspTec.isLoading ? (
          <div className="text-xs text-fg-subtle">Cargando especificaciones técnicas…</div>
        ) : (
          renderEspTec(norma, onChange, qEspTec.data ?? [])
        )}
      </div>
    </Section>
  );
}

function renderEspTec(norma: NormaForm, onChange: (n: NormaForm) => void, catalogo: EspTecCatalogo[]): React.ReactNode {
  if (norma.normaCodigo === NOM_221_P1) {
    return (
      <Field label="El equipo terminal móvil cuenta con receptor FM">
        <div className="flex gap-2">
          {[{ v: true, l: "Sí" }, { v: false, l: "No" }].map((o) => (
            <button key={String(o.v)} type="button"
                    onClick={() => onChange({ ...norma, receptorFm: o.v })}
                    className={`cursor-pointer rounded border px-4 py-1.5 text-sm transition ${
                      norma.receptorFm === o.v
                        ? "border-primary bg-primary text-white"
                        : "border-edge-strong bg-surface hover:bg-surface-2"
                    }`}>
              {o.l}
            </button>
          ))}
        </div>
      </Field>
    );
  }

  const ets = norma.especificaciones;
  if (ets.length === 0 && !catalogo.some((c) => c.dinamica)) {
    return <div className="text-xs text-fg-subtle">Esta norma no requiere especificaciones técnicas adicionales.</div>;
  }
  const tipoUi = catalogo[0]?.tipoUi ?? "TEXTAREA";

  const setEt = (uiId: string, patch: Partial<EspecForm>) =>
    onChange({
      ...norma,
      especificaciones: norma.especificaciones.map((e) => (e.uiId === uiId ? { ...e, ...patch } : e)),
    });

  if (tipoUi === "TEXTAREA_3COLS") {
    // NOM-208: 3 columnas (salto frec / mod digital / híbrido)
    return (
      <div className="w-full overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
          <tr>
            <th className="px-2 py-1 text-left">Especificación técnica</th>
            <th className="px-2 py-1 text-left">Salto de frecuencia</th>
            <th className="px-2 py-1 text-left">Modulación digital</th>
            <th className="px-2 py-1 text-left">Híbrido</th>
          </tr>
        </thead>
        <tbody>
          {ets.map((e) => (
            <tr key={e.uiId} className="border-t">
              <td className="px-2 py-1 text-xs font-medium text-fg">{e.nombre}</td>
              <td className="px-2 py-1">
                <InputConUnidades value={e.saltoFrec} unidades={e.unidades}
                                  onChange={(v) => setEt(e.uiId, { saltoFrec: v })}
                                  className="w-full rounded border px-1.5 py-1 text-xs" />
              </td>
              <td className="px-2 py-1">
                <InputConUnidades value={e.modulacionDigital} unidades={e.unidades}
                                  onChange={(v) => setEt(e.uiId, { modulacionDigital: v })}
                                  className="w-full rounded border px-1.5 py-1 text-xs" />
              </td>
              <td className="px-2 py-1">
                {/* Híbrido se captura sin sugerencias de unidad: así era en la
                    implementación validada, donde es un campo de texto simple. */}
                <input type="text" value={e.hibrido}
                       onChange={(ev) => setEt(e.uiId, { hibrido: ev.target.value })}
                       className="w-full rounded border px-1.5 py-1 text-xs" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    );
  }

  if (tipoUi === "DINAMICA_BANDA_SAR") {
    // DT-IFT-012: tabla dinámica banda + SAR
    return (
      <div>
        <button type="button"
                onClick={() => {
                  const catEntry = catalogo[0];
                  onChange({
                    ...norma,
                    especificaciones: [
                      ...norma.especificaciones,
                      {
                        uiId: Math.random().toString(36).slice(2),
                        normaEspTecId: catEntry?.id ?? null,
                        codigo: null, nombre: null,
                        tipoUi: "DINAMICA_BANDA_SAR",
                        consec: norma.especificaciones.length + 1,
                        unidades: catEntry?.unidades ?? [],
                        valor: "", saltoFrec: "", modulacionDigital: "", hibrido: "",
                        bandaFrecuencia: "", sar: "",
                      },
                    ],
                  });
                }}
                className="mb-2 inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1 text-xs font-medium text-primary-strong transition hover:bg-primary-tint">
          <Plus className="h-3 w-3" /> Agregar banda + SAR
        </button>
        <div className="w-full overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
            <tr>
              <th className="px-2 py-1 text-left">Banda de frecuencia</th>
              <th className="px-2 py-1 text-left">SAR (W/Kg)</th>
              <th className="w-12 px-2 py-1"></th>
            </tr>
          </thead>
          <tbody>
            {ets.length === 0 ? (
              <tr><td colSpan={3} className="px-2 py-3 text-center text-xs text-fg-subtle">Sin filas. Usa "Agregar banda + SAR".</td></tr>
            ) : ets.map((e) => (
              <tr key={e.uiId} className="border-t">
                <td className="px-2 py-1">
                  <InputConUnidades value={e.bandaFrecuencia} unidades={e.unidades}
                                    onChange={(v) => setEt(e.uiId, { bandaFrecuencia: v })}
                                    className="w-full rounded border px-1.5 py-1 text-xs" />
                </td>
                <td className="px-2 py-1">
                  <InputConUnidades value={e.sar} unidades={e.unidades}
                                    onChange={(v) => setEt(e.uiId, { sar: v })}
                                    className="w-full rounded border px-1.5 py-1 text-xs" />
                </td>
                <td className="px-2 py-1 text-right">
                  <button type="button"
                          onClick={() => onChange({ ...norma, especificaciones: norma.especificaciones.filter((x) => x.uiId !== e.uiId) })}
                          className="cursor-pointer rounded p-1 text-estado-err transition hover:bg-estado-err-bg"><Trash2 className="h-3 w-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  // TEXTAREA / SELECT_BANDA_28 / SELECT_VOLTE / ET_VALOR — fila ET + valor
  return (
    <div className="w-full overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-surface-2/80 text-xs uppercase text-fg-subtle">
        <tr>
          <th className="px-2 py-1 text-left" style={{ width: "30%" }}>Especificación técnica</th>
          <th className="px-2 py-1 text-left">Valor</th>
        </tr>
      </thead>
      <tbody>
        {ets.map((e) => (
          <tr key={e.uiId} className="border-t">
            <td className="px-2 py-1 text-xs font-medium text-fg">{e.nombre}</td>
            <td className="px-2 py-1">
              {e.tipoUi === "SELECT_BANDA_28" ? (
                <select value={e.valor} onChange={(ev) => setEt(e.uiId, { valor: ev.target.value })}
                        className="w-full rounded border px-1.5 py-1 text-xs">
                  <option value="">— Selecciona —</option>
                  <option value="SI_SOPORTA">Sí soporta y tiene habilitada la banda 28 (700 MHz)</option>
                  <option value="NO_SOPORTA">No soporta</option>
                </select>
              ) : e.tipoUi === "SELECT_VOLTE" ? (
                <select value={e.valor} onChange={(ev) => setEt(e.uiId, { valor: ev.target.value })}
                        className="w-full rounded border px-1.5 py-1 text-xs">
                  <option value="">— Selecciona —</option>
                  <option value="SI_VOLTE">Sí tiene habilitadas las llamadas de voz mediante VoLTE</option>
                  <option value="NO_SOPORTA">No soporta</option>
                </select>
              ) : (
                <InputConUnidades value={e.valor} unidades={e.unidades}
                                  onChange={(v) => setEt(e.uiId, { valor: v })}
                                  className="w-full rounded border px-1.5 py-1 text-xs" />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function AcreditacionGroup({
  label, items, selected, onToggle,
}: { label: string; items: Acreditacion[]; selected: string[]; onToggle: (id: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((a) => {
          const sel = selected.includes(a.id);
          return (
            <button key={a.id} type="button" onClick={() => onToggle(a.id)}
                    className={`cursor-pointer rounded border px-2 py-1 text-xs transition ${
                      sel ? "border-primary-strong bg-primary-tint text-primary-strong" : "border-edge-strong hover:bg-surface-2"
                    }`}>
              <span className="font-mono">{a.identificador}</span>
              {a.fechaEmision && <span className="ml-1 text-[10px] text-fg-subtle">{a.fechaEmision}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Input que muestra un dropdown con las unidades del catálogo al presionar
 * la tecla derecha (→) cuando el cursor está al final del valor. Inserta la
 * unidad seleccionada al final del texto.
 *
 * Espejo del completeMethod del portlet legacy: ahí teclear "k" mostraba kHz,
 * "m" mostraba MHz/ms, etc. Aquí usamos el array de unidades del catálogo
 * (kernel.norma_esp_tec.unidades) y filtramos por prefijo de la última palabra.
 */
function InputConUnidades({
  value,
  onChange,
  unidades,
  className,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  unidades: string[];
  className?: string;
  placeholder?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  // Índice de la opción resaltada para navegación con ↑/↓ (-1 = ninguna).
  const [activo, setActivo] = useState(-1);
  const inputRef = useState<{ ref: HTMLInputElement | null }>({ ref: null })[0];
  const listRef = useState<{ ref: HTMLUListElement | null }>({ ref: null })[0];

  // Sugerencias: si hay texto, filtra por la última palabra (a-z); si no, todas.
  const sug = useMemo(() => {
    if (!unidades || unidades.length === 0) return [];
    const m = /([A-Za-zµ/][A-Za-zµ0-9/_.-]*)$/.exec(value);
    if (!m) return unidades;
    const pref = m[1].toLowerCase();
    return unidades.filter((u) => u.toLowerCase().startsWith(pref));
  }, [unidades, value]);

  // Si cambian las sugerencias (al teclear), reinicia el resaltado.
  useEffect(() => setActivo(-1), [sug]);

  const insertar = (u: string) => {
    // Reemplaza la última "palabra" tipeada por la unidad completa.
    const m = /([A-Za-zµ/][A-Za-zµ0-9/_.-]*)$/.exec(value);
    const base = m ? value.slice(0, m.index) : value;
    const sep = base.length > 0 && !base.endsWith(" ") ? " " : "";
    onChange(base + sep + u);
    setAbierto(false);
    setActivo(-1);
  };

  // Mueve el resaltado, abriendo el dropdown si aún no lo está, y mantiene la
  // opción activa visible dentro del contenedor con scroll.
  const mover = (delta: number) => {
    if (sug.length === 0) return;
    if (!abierto) {
      setAbierto(true);
      setActivo(delta > 0 ? 0 : sug.length - 1);
      return;
    }
    setActivo((i) => {
      const next = i < 0 ? (delta > 0 ? 0 : sug.length - 1) : (i + delta + sug.length) % sug.length;
      const el = listRef.ref?.children[next] as HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
      return next;
    });
  };

  return (
    <div className="relative">
      <input
        ref={(el) => (inputRef.ref = el)}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        role="combobox"
        aria-expanded={abierto}
        aria-autocomplete="list"
        onKeyDown={(e) => {
          if (e.key === "ArrowRight" && unidades.length > 0) {
            const el = e.currentTarget;
            if (el.selectionStart != null && el.selectionStart === el.value.length) {
              e.preventDefault();
              setAbierto(true);
              setActivo(-1);
            }
          } else if (e.key === "ArrowDown" && unidades.length > 0) {
            e.preventDefault();
            mover(1);
          } else if (e.key === "ArrowUp" && unidades.length > 0) {
            e.preventDefault();
            mover(-1);
          } else if (e.key === "Enter" && abierto && activo >= 0 && activo < sug.length) {
            e.preventDefault();
            insertar(sug[activo]);
          } else if (e.key === "Escape") {
            setAbierto(false);
            setActivo(-1);
          }
        }}
        onBlur={() => setTimeout(() => setAbierto(false), 120)}
        placeholder={placeholder ?? (unidades.length > 0 ? "→ o ↓ para sugerencias" : "")}
        className={className}
      />
      {abierto && sug.length > 0 && (
        <ul
          ref={(el) => (listRef.ref = el)}
          role="listbox"
          className="absolute left-0 right-0 top-full z-10 max-h-44 overflow-y-auto rounded border bg-surface text-xs shadow-overlay"
        >
          {sug.map((u, i) => (
            <li key={u}
                role="option"
                aria-selected={i === activo}
                onMouseDown={(e) => { e.preventDefault(); insertar(u); }}
                onMouseEnter={() => setActivo(i)}
                className={`cursor-pointer px-2 py-1 ${i === activo ? "bg-primary-tint" : "hover:bg-surface-2"}`}>
              {u}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Sección del wizard sobre el primitivo `Card`. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card title={title} bodyClassName="space-y-3">
      {children}
    </Card>
  );
}

/** Campo etiquetado del wizard sobre `FormField`. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <FormField label={label}>{children}</FormField>;
}

function ReadField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <DataField label={label} value={mono ? <span className="font-mono">{value}</span> : value} />;
}
